// src/components/CoachHistorySheet.js
//
// The coach's earlier conversation, as a sheet. It used to be a toggle inside
// the thread ("Earlier conversation · 18") that unfolded a wall of bubbles
// above whatever you were doing. Here it is a record: every exchange, newest
// first, under the day it happened, each one a row that opens in place and
// can be asked again. The thread on the Coach screen is this visit only.

import React, { useEffect, useRef, useState } from "react";
import { X, ChevronDown } from "lucide-react";
import { useSheetPull } from "./useSheetPull";
import { renderCoachText, plainCoachText } from "./coachText";

const dayKey = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const yesterdayKey = (now) => { const y = new Date(now); y.setDate(y.getDate() - 1); return dayKey(+y); };
export function dayLabel(ms, now = Date.now()) {
  if (dayKey(ms) === dayKey(now)) return "Today";
  if (dayKey(ms) === yesterdayKey(now)) return "Yesterday";
  const d = new Date(ms);
  const opts = { weekday: "short", day: "numeric", month: "short" };
  const s = d.toLocaleDateString("en-GB", opts);
  return d.getFullYear() !== new Date(now).getFullYear() ? `${s} ${d.getFullYear()}` : s;
}
export function timeLabel(ms) {
  return new Date(ms).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
}
export function sinceLabel(ms, now = Date.now()) {
  if (dayKey(ms) === dayKey(now)) return "today";
  if (dayKey(ms) === yesterdayKey(now)) return "since yesterday";
  const d = new Date(ms);
  const s = d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `since ${s}${d.getFullYear() !== new Date(now).getFullYear() ? ` ${d.getFullYear()}` : ""}`;
}

/* A flat list of messages becomes exchanges (a question and whatever the
   coach said back), decisions (system lines) in their place, newest first,
   grouped by the day they happened. */
export function buildHistory(messages, now = Date.now()) {
  const list = (messages || []).slice().sort((a, b) => (Number(a.createdAt) || 0) - (Number(b.createdAt) || 0) || (a.id > b.id ? 1 : -1));
  const items = [];
  let open = null;
  let firstAt = null;
  for (const m of list) {
    const at = Number(m.createdAt) || 0;
    if (firstAt == null || at < firstAt) firstAt = at;
    if (m.role === "user") { open = { kind: "x", id: m.id, q: String(m.content || ""), a: [], at }; items.push(open); }
    else if (m.role === "assistant") {
      if (open) open.a.push(String(m.content || ""));
      else { open = { kind: "x", id: m.id, q: null, a: [String(m.content || "")], at }; items.push(open); }
    } else if (m.role === "system") { items.push({ kind: "sys", id: m.id, text: String(m.content || ""), at }); open = null; }
  }
  items.reverse();
  const days = [];
  for (const it of items) {
    const key = dayKey(it.at);
    const last = days[days.length - 1];
    if (last && last.key === key) last.items.push(it);
    else days.push({ key, label: dayLabel(it.at, now), items: [it] });
  }
  return {
    days,
    exchanges: items.filter((i) => i.kind === "x").length,
    decisions: items.filter((i) => i.kind === "sys").length,
    firstAt,
  };
}

export default function CoachHistorySheet({ messages, onClose, onAskAgain, sending, capped }) {
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);
  const overlayRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useSheetPull({ sheetRef, bodyRef, overlayRef, closeRef, dirtyRef: null });
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (sheetRef.current) { try { sheetRef.current.focus({ preventScroll: true }); } catch (_) {} }
  }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeRef.current && closeRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const h = buildHistory(messages);
  const n = h.exchanges;
  const sub1 = n > 0 ? `${n} ${n === 1 ? "exchange" : "exchanges"}` : h.decisions > 0 ? `${h.decisions} ${h.decisions === 1 ? "decision" : "decisions"}` : "Nothing yet";
  const sub2 = n === 0 && h.decisions === 0 ? null : capped ? "last 60 messages" : h.firstAt != null ? sinceLabel(h.firstAt) : null;
  const toggle = (id) => setOpenId((p) => (p === id ? null : id));

  return (
    <div className="bottomSheetOverlay" ref={overlayRef} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={sheetRef}
        className="bottomSheetPanel tsSheet chSheet"
        style={{ "--type": "var(--accent-base)" }}
        role="dialog"
        aria-label="History"
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="bottomSheetDragHandle" aria-hidden="true" />
        <div className="tsHead">
          <div className="tsHead__spine" aria-hidden="true" />
          <div className="tsHead__text">
            <div className="tsHead__title">History</div>
            <div className="tsHead__date">
              <span className="chSub__n">{sub1}</span>
              {sub2 ? <><span className="tsHead__sep" /><span className="chSub__since">{sub2}</span></> : null}
            </div>
          </div>
          <button type="button" className="bottomSheetClose" onClick={onClose} aria-label="Close"><X size={16} aria-hidden="true" /></button>
        </div>
        <div ref={bodyRef} className="bottomSheetBody tsBody chBody">
          {h.days.length === 0 ? <div className="chEmpty">Nothing here yet.</div> : null}
          {h.days.map((day) => (
            <section key={day.key} className="chDayGroup">
              <div className="chDay">{day.label}</div>
              {day.items.map((it) => {
                if (it.kind === "sys") {
                  return (
                    <div key={`s-${it.id}`} className="chSys">
                      <span className="chSys__dot" aria-hidden="true" />
                      <span className="chSys__text">{it.text}</span>
                      <span className="chSys__time">{timeLabel(it.at)}</span>
                    </div>
                  );
                }
                const isOpen = openId === it.id;
                const preview = plainCoachText(it.a.join(" "));
                return (
                  <article key={`x-${it.id}`} className={"chX" + (isOpen ? " is-open" : "")}>
                    <div
                      className="chX__hit"
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      onClick={() => toggle(it.id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(it.id); } }}
                    >
                      <div className="chX__head">
                        <div className="chX__q">{it.q != null && it.q !== "" ? it.q : "Coach"}</div>
                        <span className="chX__time">{timeLabel(it.at)}</span>
                        <ChevronDown size={14} className="chX__chev" aria-hidden="true" />
                      </div>
                      {it.a.length > 0 ? (
                        <div className="chX__a">
                          {isOpen ? it.a.map((t, i) => <p key={i} className="chX__p">{renderCoachText(t)}</p>) : preview}
                        </div>
                      ) : null}
                    </div>
                    {isOpen && it.q ? (
                      <div className="chX__foot">
                        <button type="button" className="chX__again" onClick={() => onAskAgain(it.q)} disabled={!!sending}>Ask again</button>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </section>
          ))}
          {capped ? <div className="chNote">Showing the last 60 messages.</div> : null}
        </div>
      </div>
    </div>
  );
}
